/**
 * Termly cookie consent for the hosted storefront. boxed parity (`pages/_document.tsx` and
 * `hooks/shared/useTermlyConsent.ts` there): one resource-blocker script per page load with the
 * autoblocker on, and a hook that reads the shopper's consent off `window.Termly`. Loaded only
 * when the config resolves a Termly website (the environment's own or the platform default), so
 * a stage without one loads nothing from Termly. Never throws and never blocks the shopper.
 * Consent is Termly's own: it lives in this origin's localStorage, so every storefront hostname
 * asks on its own — docs/storefront.md, "Cookie consent".
 */
import type { StorefrontTermly } from "@loot-os/api-contract";
import { useEffect, useState } from "react";

import type { TenantConfig } from "./tenant.js";

declare global {
  interface Window {
    /** Termly's runtime, assigned synchronously when the resource-blocker script evaluates. */
    Termly?: {
      getConsentState?: () => Record<string, boolean>;
      on?: (event: "initialized" | "consent", cb: (data: unknown) => void) => void;
      off?: (event: "initialized" | "consent", cb: (data: unknown) => void) => void;
    };
    /** Opens Termly's preference modal; set only when the website's preference widget is on. */
    displayPreferenceModal?: () => void;
  }
}

/** The consent categories a gated script checks; both off until Termly says otherwise. */
export interface ConsentState {
  analytics: boolean;
  advertising: boolean;
}

const OFF: StorefrontTermly = { websiteUuid: "", theme: {} };
const NO_CONSENT: ConsentState = { analytics: false, advertising: false };

/**
 * Fail-closed read: a config cached by an older BFF or in sessionStorage has no `termly`, and
 * one from the first release has it without `theme`.
 */
export function termlyOf(config: TenantConfig): StorefrontTermly {
  const termly = (config as Partial<TenantConfig>).termly as Partial<StorefrontTermly> | undefined;
  if (!termly) return OFF;
  return { websiteUuid: termly.websiteUuid ?? "", theme: termly.theme ?? {} };
}

/**
 * Termly's autoblocker holds third-party scripts until the shopper consents, as boxed runs it.
 * Every script the storefront injects itself carries `data-categories="essential"` so nothing the
 * store needs waits on a banner; a vendor's own tag (Intercom's) needs a domain rule on the Termly
 * website instead.
 */
export const TERMLY_AUTO_BLOCK = true;

/** What Termly's script accepts as its website segment; anything else is refused, never escaped. */
const WEBSITE_UUID = /^[A-Za-z0-9-]+$/;

/**
 * The resource-blocker URL for a website, or null for an id Termly itself would reject. Built
 * from parts, so the id can never smuggle a query — in particular `masterConsentsOrigin`, which
 * would share one consent across every storefront.
 */
export function termlyScriptUrl(websiteUuid: string): string | null {
  if (!WEBSITE_UUID.test(websiteUuid)) return null;
  const url = new URL(`https://app.termly.io/resource-blocker/${websiteUuid}`);
  if (TERMLY_AUTO_BLOCK) url.searchParams.set("autoBlock", "on");
  return url.toString();
}

let loading: Promise<boolean> | null = null;
let settle: ((loaded: boolean) => void) | null = null;
/** Resolves once the script has loaded or failed; never, on a page that loads none. */
const ready = new Promise<boolean>((resolve) => {
  settle = resolve;
});

/**
 * Injects the resource-blocker once per page load — the first website wins, so StrictMode's
 * doubled effect and a later config refetch are no-ops — and resolves whether it loaded. Never
 * throws.
 */
export function loadTermly(websiteUuid: string): Promise<boolean> {
  if (typeof window === "undefined") return Promise.resolve(false);
  loading ??= new Promise<boolean>((resolve) => {
    const src = termlyScriptUrl(websiteUuid);
    if (!src) {
      resolve(false);
      return;
    }
    try {
      const script = document.createElement("script");
      script.async = true;
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    } catch {
      resolve(false);
    }
  }).then((loaded) => {
    settle?.(loaded);
    return loaded;
  });
  return loading;
}

function readConsent(): ConsentState {
  const raw = window.Termly?.getConsentState?.();
  if (!raw) return NO_CONSENT;
  return { analytics: Boolean(raw.analytics), advertising: Boolean(raw.advertising) };
}

/**
 * Subscribes to the shopper's consent: called with the current state once Termly is up and
 * again on every change, never before, and never at all on a page that loads no Termly.
 */
export function onTermlyConsent(listener: (state: ConsentState) => void): () => void {
  let disposed = false;
  const handler = () => {
    if (!disposed) listener(readConsent());
  };
  void ready.then((loaded) => {
    if (!loaded || disposed) return;
    window.Termly?.on?.("initialized", handler);
    window.Termly?.on?.("consent", handler);
    handler();
  });
  return () => {
    disposed = true;
    window.Termly?.off?.("initialized", handler);
    window.Termly?.off?.("consent", handler);
  };
}

/** boxed's `useTermlyConsent`: the categories a consent-gated script checks before it loads. */
export function useTermlyConsent(): ConsentState {
  const [consent, setConsent] = useState<ConsentState>(NO_CONSENT);
  useEffect(
    () =>
      onTermlyConsent((next) =>
        setConsent((prev) =>
          prev.analytics === next.analytics && prev.advertising === next.advertising ? prev : next,
        ),
      ),
    [],
  );
  return consent;
}
